import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { api } from '@/lib/api';
import { usePagedQuery } from '@/lib/paged';
import { normalizeCatalogPage } from '@/lib/normalize';
import type { DatasetSummary } from '@/lib/contract';
import { formatDay, formatHours, orDash, statusLabel, statusTone } from '@/lib/format';
import { PageHeader } from '@/ui/page-header';
import { useRealtimeRefresh } from '@/lib/realtime';
import { DatasetsTabs } from './datasets-layout';
import { Segmented } from '@/ui/segmented';
import { FilterChip } from '@/ui/filter-chip';
import { MicroLabel } from '@/ui/micro-label';
import { EmptyState } from '@/ui/empty-state';
import { ErrorState } from '@/ui/error-state';
import { StatusDot } from '@/ui/status-dot';
import { DataTable, type Column } from '@/ui/data-table';
import { LoadMore } from '@/ui/load-more';
import { GateBadge } from '@/domain/gate-badge';
import { ModalityChips } from '@/domain/modality-chips';
import { QualityBlocks, QualityBlocksLegend } from '@/domain/quality-blocks';

const FILTERS = [
  { key: 'vendor', label: 'Vendor' },
  { key: 'modality', label: 'Modality' },
  { key: 'stage', label: 'Stage' },
  { key: 'gate', label: 'Gate' }
] as const;

const ALL = [{ value: 'all', label: 'All' }];

const VIEWS = [
  { value: 'table', label: 'Table' },
  { value: 'kanban', label: 'Kanban' },
  { value: 'pipeline', label: 'Pipeline' }
];

/**
 * Every dataset in flight, across every vendor.
 *
 * This is the operator's working list, and until now it did not exist: `/admin/datasets/submissions`
 * was a stub while `/admin/datasets` rendered *catalog* cards — certified-product cards, availability
 * badge and all — over this same unfiltered query. So a batch still uploading appeared as a product
 * with a blank badge and "Cert —", and the one screen that wanted a stage, a gate and a vendor name
 * had nowhere to show them.
 *
 * The columns are the vendor's own submissions table (`screens/vendor/submissions.tsx`) plus the two
 * things only an operator needs: whose dataset it is, and what modalities it holds.
 */
export function DatasetsSubmissions() {
  const [view, setView] = useState('table');
  const [selected, setSelected] = useState<Record<string, string>>({});
  const navigate = useNavigate();

  /**
   * The filters are part of the query key, so narrowing re-reads the server rather than hiding rows
   * the page happens to hold. Filtering client-side would have been quietly wrong under keyset
   * pagination: a page of thirty might contain two matches, and the table would call that the answer.
   */
  const query = useMemo(() => {
    const params = new URLSearchParams();
    for (const { key } of FILTERS) if (selected[key] && selected[key] !== 'all') params.set(key, selected[key]);
    return params.toString();
  }, [selected]);

  const page = usePagedQuery({
    queryKey: ['admin', 'datasets', 'submissions', query],
    fetchPage: async cursor => {
      const params = new URLSearchParams(query);
      if (cursor) params.set('cursor', cursor);
      const search = params.toString();
      return normalizeCatalogPage(await api(`/datasets${search ? `?${search}` : ''}`));
    },
    /**
     * Cross-tenant, and the realtime channels are per tenant, so there is no subscription an
     * operator could hold that covers this list. It polls faster only while something is moving.
     */
    refetchInterval: rows => {
      const moving = rows.some(r => ['uploading', 'evaluating', 'ready_for_evaluation'].includes(r.lifecycle_status));
      return moving ? 15000 : 60000;
    }
  });
  const { isPending, isError, error, refetch } = page;

  /**
   * The operator's own channel.
   *
   * Every other dataset channel is per tenant, and these listings are cross-tenant -- so this screen
   * held no subscription at all and learned about a finished run only on its next poll, up to a
   * minute later. For the one table whose job is "what needs a decision", that is the wrong minute.
   */
  useRealtimeRefresh(
    '/datasets/subscription-token',
    [
      ['admin', 'datasets', 'submissions'],
      ['admin', 'datasets', 'catalog']
    ],
    { realm: 'admin' }
  );

  const rows = page.rows;
  // Totals and filter options are stated once, by the first page: they describe the whole listing,
  // not the slice that has been loaded.
  const totals = page.first?.totals;

  const columns = useMemo<Column<DatasetSummary>[]>(
    () => [
      {
        key: 'name',
        header: 'Dataset',
        className: 'w-[190px]',
        render: row => <span className="font-mono text-[13px] text-foreground">{row.name}</span>
      },
      {
        key: 'vendor',
        header: 'Vendor',
        className: 'w-[130px]',
        render: row => <span className="text-[13px] text-foreground">{orDash(row.vendor_name)}</span>
      },
      {
        key: 'hours',
        header: 'Hours',
        className: 'w-[70px]',
        // Measured, never declared. A dash means nothing has read the bytes yet.
        render: row => <span className="font-mono text-[13px]">{formatHours(row.hours)}</span>
      },
      {
        key: 'modalities',
        header: 'Modalities',
        className: 'w-[150px]',
        render: row => <ModalityChips modalities={row.modalities} />
      },
      {
        key: 'format',
        header: 'Format',
        className: 'w-[110px]',
        render: row => (
          <MicroLabel tone="muted" size="xs" caps={false}>
            {orDash(row.format_label)}
          </MicroLabel>
        )
      },
      {
        key: 'stage',
        header: 'Stage',
        className: 'w-[140px]',
        render: row => <StatusDot tone={statusTone(row.lifecycle_status)} label={statusLabel(row.lifecycle_status)} />
      },
      {
        key: 'gate',
        header: 'Gate',
        className: 'w-[70px]',
        render: row => <GateBadge gate={row.gate} />
      },
      {
        /**
         * The mockup labels this "DIM ROLLUP  S·E·A·N·O". Those initials predate the rename recorded
         * in packages/contracts ("the vocabulary is ours, not the QA vendor's") — our five blocks are
         * Core structural, Modality overlays, High-stakes governance, Payload QA readiness, Fitness.
         * Printing the old initials over the new data would leave this header disagreeing with the
         * legend directly beneath the table.
         */
        key: 'quality',
        header: 'Quality  C·M·H·P·F',
        className: 'w-[140px]',
        render: row => <QualityBlocks blocks={row.quality_blocks} size="sm" />
      },
      {
        key: 'submitted',
        header: 'Submitted',
        className: 'w-[100px]',
        render: row => (
          <MicroLabel tone="muted" size="xs" caps={false}>
            {formatDay(row.submitted_at)}
          </MicroLabel>
        )
      },
      {
        key: 'chevron',
        header: '',
        className: 'w-8 text-right',
        render: () => <ChevronRight className="ml-auto size-3.5 text-dim" />
      }
    ],
    []
  );

  return (
    <div className="grid gap-5">
      <PageHeader
        title="Datasets"
        subtitle={
          totals
            ? `${totals.dataset_count} datasets · ${totals.in_pipeline_count} in pipeline · ${totals.certified_product_count} certified`
            : 'Submissions'
        }
        actions={<Segmented segments={VIEWS} value={view} onChange={setView} />}
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <DatasetsTabs className="flex-1" />
        <div className="flex flex-wrap items-center gap-2 pb-3">
          {FILTERS.map(({ key, label }) => (
            <FilterChip
              key={key}
              label={label}
              value={selected[key] ?? 'all'}
              options={[
                ...ALL,
                // The server returns the raw enum; `statusLabel` is what every other stage on the
                // screen is rendered through, so the chip and the column agree on wording.
                ...(page.first?.filter_options?.[key] ?? []).map(option =>
                  key === 'stage' ? { ...option, label: statusLabel(option.value) } : option
                )
              ]}
              onChange={value => setSelected(prev => ({ ...prev, [key]: value }))}
            />
          ))}
        </div>
      </div>

      {isError ? (
        <ErrorState error={error} onRetry={() => void refetch()} title="Could not load the submissions" />
      ) : view !== 'table' ? (
        <EmptyState title={`The ${view} view has no design yet.`} note="Screen intentionally left empty" />
      ) : (
        <>
          <DataTable
            columns={columns}
            rows={rows}
            rowKey={row => row.id}
            onRowClick={row => navigate(`/admin/datasets/${row.id}?tenant=${row.tenant_id}`)}
            empty={isPending ? 'Loading…' : 'No datasets yet.'}
            footer={<QualityBlocksLegend />}
          />
          <LoadMore
            shown={rows.length}
            total={totals?.dataset_count}
            hasMore={page.hasMore}
            loading={page.loadingMore}
            onLoadMore={page.loadMore}
          />
        </>
      )}
    </div>
  );
}
